import { useState } from 'react';
import { Send, Check, Loader2, Phone, Mail, User, Wrench } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function LeadCaptureForm() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', service_interest: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email) { setError('Email is required'); return; }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/api/leads/capture`, form);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-8" data-testid="lead-success">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">We'll be in touch!</h3>
        <p className="text-gray-600">A member of our team will reach out shortly.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="lead-capture-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Your name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="pl-10 h-12 bg-white border-gray-200"
            data-testid="lead-name"
          />
        </div>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="email"
            placeholder="Email address *"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="pl-10 h-12 bg-white border-gray-200"
            required
            data-testid="lead-email"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="tel"
            placeholder="Phone number"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="pl-10 h-12 bg-white border-gray-200"
            data-testid="lead-phone"
          />
        </div>
        <div className="relative">
          <Wrench className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <select
            value={form.service_interest}
            onChange={(e) => setForm({ ...form, service_interest: e.target.value })}
            className="w-full h-12 pl-10 pr-4 rounded-md border border-gray-200 bg-white text-sm text-gray-700 focus:ring-2 focus:ring-[#FF3008]/20 focus:border-[#FF3008]"
            data-testid="lead-service"
          >
            <option value="">Service interested in</option>
            <option value="roadside">Roadside Assistance</option>
            <option value="mechanic">Mobile Mechanic</option>
            <option value="towing">Towing</option>
            <option value="delivery">Get it Delivered</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>
      {error && <p className="text-red-500 text-sm" data-testid="lead-error">{error}</p>}
      <Button
        type="submit"
        disabled={loading}
        className="w-full h-12 bg-[#FF3008] hover:bg-[#E02800] text-white font-semibold text-base rounded-full"
        data-testid="lead-submit"
      >
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
          <>
            <Send className="w-4 h-4 mr-2" />
            Get a Free Quote
          </>
        )}
      </Button>
      <p className="text-xs text-gray-400 text-center">No spam. We'll only contact you about your service request.</p>
    </form>
  );
}
